import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  getQbQuote,
  updateQbQuote,
  replaceDaySlots,
  recalculateQbQuote,
  addQbLine,
  updateQbLine,
  deleteQbLine,
  sendQbQuote,
  acceptQbQuote,
  cancelQbQuote,
  copyQbQuote,
} from "../../api/quotes";
import { listLocations } from "../../api/locations";
import { listSeasons } from "../../api/seasons";
import useDebouncedSave from "../../utils/useDebouncedSave";
import QuoteHeaderForm from "./QuoteHeaderForm";
import DayGridEditor from "../DayGridEditor";
import LinesTable from "./LinesTable";
import AdHocLineForm from "./AdHocLineForm";
import TotalsPanel from "./TotalsPanel";
import ActionBar from "./ActionBar";
import { renderQuotePdf } from "./quotePdf";

const statusColor = (s) => {
  switch (s) {
    case "ACCEPTED": return "success";
    case "SENT": return "info";
    case "EXPIRED":
    case "CANCELLED": return "default";
    default: return "warning";
  }
};

export default function QuoteDetail({ id, onBack, onOpenQuote }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [locations, setLocations] = useState([]);
  const [seasons, setSeasons] = useState([]);
  // Placeholder arrival-day-of-week for the day grid when there's no startDate.
  // Stored in localStorage by QuoteHeaderForm; lifted here so DayGridEditor can label rows.
  const [arrivalDow, setArrivalDow] = useState(null);
  const [savedAt, setSavedAt] = useState(null); // last successful save, for the "All changes saved" line

  const isDraft = quote?.status === "DRAFT";
  const readOnly = !isDraft;

  // Latest-quote ref so debounced savers can read current daySlots/nights without stale closure.
  const quoteRef = useRef(quote);
  useEffect(() => { quoteRef.current = quote; }, [quote]);

  // ---- loaders ----
  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const q = await getQbQuote(id);
      setQuote(q);
      // Catalogue edits (a new price row, a changed rule) only reach a quote on recalc,
      // so a draft reprices as it opens. If it can't be priced, show why and keep the saved copy.
      if (q?.status === "DRAFT") {
        try {
          setQuote(await recalculateQbQuote(id));
        } catch (e) {
          setError(e.message || "Couldn't reprice this quote");
        }
      }
    } catch (e) {
      setError(e.message || "Failed to load quote");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    listLocations({ activeOnly: false }).then((d) => setLocations(d || [])).catch(() => {});
    listSeasons({ activeOnly: false }).then((d) => setSeasons(d || [])).catch(() => {});
  }, []);

  // ---- header debounced save ----
  const headerSaver = useDebouncedSave(async (body) => {
    // If nights is being reduced, prune day slots beyond the new departure day
    // first — otherwise the backend rejects the header PUT for inconsistency.
    const current = quoteRef.current;
    if (body.nights != null && current) {
      const newMaxDay = Number(body.nights) + 1;
      if (Number.isFinite(newMaxDay) && Number(body.nights) < (current.nights ?? Infinity)) {
        const pruned = (current.daySlots || []).filter((s) => s.dayIndex <= newMaxDay);
        if (pruned.length !== (current.daySlots || []).length) {
          await replaceDaySlots(id, pruned);
        }
      }
    }
    const updated = await updateQbQuote(id, body);
    setQuote(updated);
    setSavedAt(new Date());
  }, 500);

  // ---- day grid debounced save ----
  const gridSaver = useDebouncedSave(async (slots) => {
    const updated = await replaceDaySlots(id, slots);
    setQuote(updated);
    setSavedAt(new Date());
  }, 500);

  const handleGridChange = (next) => {
    setQuote((prev) => (prev ? { ...prev, daySlots: next } : prev));
    gridSaver.schedule(next);
  };

  const handleMarkupChange = async (markupPct) => {
    try {
      setError("");
      await headerSaver.flush();
      await gridSaver.flush();
      const q = quoteRef.current;
      if (!q) return;
      const body = {
        clientName: q.clientName,
        clientEmail: q.clientEmail,
        clientAddress: q.clientAddress,
        seasonId: q.seasonId,
        locationId: q.locationId,
        monthHint: q.monthHint,
        startDate: q.startDate,
        endDate: q.endDate,
        nights: q.nights,
        studentPax: q.studentPax,
        visitingAdultPax: q.visitingAdultPax,
        validityUntil: q.validityUntil,
        notes: q.notes,
        markupPct: String(markupPct),
      };
      const updated = await updateQbQuote(id, body);
      setQuote(updated);
    } catch (e) {
      setError(e.message || "Failed to update markup");
    }
  };

  // ---- direct line ops ----
  const addAdHoc = async (body) => {
    const updated = await addQbLine(id, body);
    setQuote(updated);
  };
  const onUpdateLine = async (lineId, body) => {
    const updated = await updateQbLine(id, lineId, body);
    setQuote(updated);
  };
  const onDeleteLine = async (lineId) => {
    if (!window.confirm("Delete this line?")) return;
    const updated = await deleteQbLine(id, lineId);
    setQuote(updated);
  };

  // ---- lifecycle ----
  const runAction = async (fn) => {
    try {
      setError("");
      const updated = await fn();
      setQuote(updated);
    } catch (e) {
      setError(e.message || "Action failed");
    }
  };
  const recalc = () => runAction(async () => {
    await headerSaver.flush();
    await gridSaver.flush();
    return recalculateQbQuote(id);
  });
  const send = () => lock(() => sendQbQuote(id));
  // Anything still waiting to autosave goes first; once the quote is locked, nothing more is saved.
  // Resolves true when everything is saved.
  const settleSaves = async () => {
    const headerOk = await headerSaver.flush();
    const gridOk = await gridSaver.flush();
    return headerOk && gridOk;
  };
  const NOT_SAVED = "Your latest changes couldn't be saved. Fix the error above and try again.";
  const lock = (fn) => runAction(async () => {
    if (!(await settleSaves())) throw new Error(NOT_SAVED);
    const updated = await fn();
    headerSaver.cancel();
    gridSaver.cancel();
    return updated;
  });
  const accept = () => lock(() => acceptQbQuote(id));
  const cancel = () => lock(() => cancelQbQuote(id));
  const unsaved = () => headerSaver.hasPending() || gridSaver.hasPending();

  // Leave the quote: save what's queued first, and only leave unsaved work if the user says so.
  const leave = async () => {
    if (isDraft && !(await settleSaves())
      && !window.confirm("Some changes couldn't be saved. Leave anyway and lose them?")) return;
    onBack();
  };
  const saveAndClose = async () => {
    try {
      setError("");
      if (!(await settleSaves())) { setError(NOT_SAVED); return; }
      setQuote(await recalculateQbQuote(id));
      onBack();
    } catch (e) {
      setError(e.message || "Save failed");
    }
  };

  // Closing the tab or reloading with unsaved changes: let the browser ask first.
  useEffect(() => {
    const warn = (e) => { if (unsaved()) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  });

  const copyToDraft = async () => {
    try {
      setError("");
      await settleSaves();
      const created = await copyQbQuote(id);
      if (created?.id) onOpenQuote?.(created.id);
    } catch (e) {
      setError(e.message || "Copy failed");
    }
  };
  const exportPdf = async () => {
    if (!quote) return;
    try {
      await renderQuotePdf(quote, { locations, seasons, arrivalDayOfWeek: arrivalDow });
    } catch (e) {
      setError(e.message || "PDF render failed");
    }
  };

  if (loading || !quote) {
    return (
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={leave}>Back</Button>
        </Stack>
        {error ? <Typography color="error">{error}</Typography> : (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {/* Status header */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <Button startIcon={<ArrowBackIcon />} onClick={leave}>Back</Button>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {quote.quoteNumber || "(draft quote)"} · {quote.clientName || "(unnamed)"}
        </Typography>
        <Chip label={quote.status} color={statusColor(quote.status)} size="small" />
        {quote.createdByName && (
          <Typography variant="caption" color="text.secondary">
            created by {quote.createdByName}
          </Typography>
        )}
        {quote.hasMissingPrices && (
          <Chip
            icon={<WarningAmberIcon />}
            label="Missing pricing"
            color="error"
            size="small"
          />
        )}
        {quote.marginBelowThreshold && (
          <Chip
            icon={<WarningAmberIcon />}
            label="Margin below threshold"
            color="warning"
            size="small"
          />
        )}
        {isDraft && (
          <SaveState
            saving={headerSaver.saving || gridSaver.saving}
            failed={!!(headerSaver.error || gridSaver.error)}
            savedAt={savedAt}
            onRetry={settleSaves}
          />
        )}
      </Stack>

      {readOnly && (
        <Alert
          severity={quote.status === "CANCELLED" ? "warning" : "info"}
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={copyToDraft}>Copy to new draft</Button>}
        >
          {quote.status === "CANCELLED"
            ? "This quote was cancelled, so it can't be edited. Copy it to a new draft to carry on from where it was."
            : `This quote is ${quote.status.toLowerCase()}, so it's locked. To make changes, copy it to a new draft.`}
        </Alert>
      )}
      {quote.parentQuoteId && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Copied from an earlier quote.{" "}
          <Button size="small" sx={{ p: 0, minWidth: 0, verticalAlign: "baseline" }} onClick={() => onOpenQuote?.(quote.parentQuoteId)}>
            Open the original
          </Button>
        </Typography>
      )}

      {quote.missingAutoTypes?.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Nothing priced for <strong>{listNames(quote.missingAutoTypes)}</strong> at this location and season, so
          {quote.missingAutoTypes.length === 1 ? " it isn't" : " they aren't"} in this quote's cost.
          Add a pricing row under Admin → Products, then recalculate.
        </Alert>
      )}

      {(error || headerSaver.error || gridSaver.error) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || headerSaver.error || gridSaver.error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            <QuoteHeaderForm
              quote={quote}
              locations={locations}
              seasons={seasons}
              disabled={readOnly}
              onChange={(body) => headerSaver.schedule(body)}
              onArrivalDowChange={setArrivalDow}
            />

            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Day grid</Typography>
              <DayGridEditor
                daySlots={quote.daySlots || []}
                nights={quote.nights || 0}
                disabled={readOnly}
                locationId={quote.locationId}
                seasonId={quote.seasonId}
                startDate={quote.startDate}
                arrivalDayOfWeek={arrivalDow}
                onChange={handleGridChange}
                onCatalogueChanged={recalc}
              />
            </Paper>

            <LinesTable
              quote={quote}
              disabled={readOnly}
              onUpdateLine={onUpdateLine}
              onDeleteLine={onDeleteLine}
            />

            {!readOnly && <AdHocLineForm onAdd={addAdHoc} disabled={readOnly} />}
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <TotalsPanel
            quote={quote}
            disabled={readOnly}
            onMarkupChange={handleMarkupChange}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <ActionBar
          status={quote.status}
          onRecalculate={recalc}
          onPdf={exportPdf}
          onSend={send}
          onAccept={accept}
          onCancel={cancel}
          onCopy={copyToDraft}
          onSaveClose={saveAndClose}
          onClose={leave}
        />
      </Box>
    </Box>
  );
}

function SaveState({ saving, failed, savedAt, onRetry }) {
  if (saving) {
    return <Typography variant="caption" color="text.secondary">Saving…</Typography>;
  }
  if (failed) {
    return (
      <Typography variant="caption" color="error.main">
        Couldn't save.{" "}
        <Button size="small" color="error" sx={{ p: 0, minWidth: 0, fontSize: "inherit" }} onClick={onRetry}>Retry</Button>
      </Typography>
    );
  }
  if (!savedAt) {
    return <Typography variant="caption" color="text.secondary">Changes save automatically</Typography>;
  }
  return (
    <Typography variant="caption" color="text.secondary">
      All changes saved · {savedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
    </Typography>
  );
}

const listNames = (names) => names.map((n) => n.toLowerCase())
  .join(", ").replace(/, ([^,]*)$/, " or $1");
